(function () {
  "use strict";

  const COMPONENT_NAME = "rcc-product-swatches";
  const API_VERSION = "2026-01";
  const swatchCache = new Map();
  const pendingRequests = new Map();
  const previewImageCache = new Map();

  class ProductSwatchesComponent extends HTMLElement {
    static get observedAttributes() {
      return ["product"];
    }

    constructor() {
      super();
      this._product = null;
      this._renderVersion = 0;
      this._interactionController = null;
      this._previewState = null;
      this._previewToken = 0;
    }

    get product() {
      return this._product;
    }

    set product(value) {
      this._product = value;
      this.render();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name !== "product" || oldValue === newValue || !newValue) return;

      try {
        this.product = JSON.parse(newValue);
      } catch (_error) {
        this.render();
      }
    }

    connectedCallback() {
      if (this._product || this.hasAttribute("product")) {
        this.render();
      }
    }

    disconnectedCallback() {
      this.teardownInteractions();
    }

    getSerializedProduct() {
      const productAttribute = this.getAttribute("product");
      if (!productAttribute) return null;

      try {
        return JSON.parse(productAttribute);
      } catch (_error) {
        return null;
      }
    }

    async render() {
      const renderVersion = ++this._renderVersion;
      const product = this._product || this.getSerializedProduct();

      if (!product) {
        this.teardownInteractions();
        this.innerHTML = "";
        this.hidden = true;
        return;
      }

      const directLinkedProducts = getArrayMetafield(
        product,
        "wardow",
        "linked_colors",
      );
      const storefrontProduct = await this.getStorefrontProduct(product);
      if (renderVersion !== this._renderVersion) return;

      const linkedProducts =
        storefrontProduct?.linkedColors?.references?.nodes ??
        directLinkedProducts;
      const currentProductKey = resolveProductCompareKey(product);
      const swatches = prioritizeCurrentSwatch(
        this.getSwatches(linkedProducts),
        currentProductKey,
      );

      if (swatches.length === 0) {
        this.teardownInteractions();
        this.innerHTML = "";
        this.hidden = true;
        return;
      }

      const visibleSwatches = swatches.slice(0, 6);
      const overflowCount = Math.max(
        swatches.length - visibleSwatches.length,
        0,
      );
      this.hidden = false;
      this.innerHTML = `
        <div class="rcc-product-swatches" aria-label="Available colors">
          ${visibleSwatches.map((swatch) => renderSwatchMarkup(swatch, currentProductKey)).join("")}
          ${overflowCount > 0 ? `<span class="rcc-product-swatches__more">+${overflowCount}</span>` : ""}
        </div>
      `;
      this.bindInteractions();
    }

    teardownInteractions() {
      this._interactionController?.abort();
      this._interactionController = null;
      this.restorePreviewImage();
    }

    bindInteractions() {
      this._interactionController?.abort();
      this._interactionController = null;

      const swatchLinks = [
        ...this.querySelectorAll(
          ".rcc-product-swatches__link[data-preview-url]",
        ),
      ];
      if (swatchLinks.length === 0) return;

      const controller = new AbortController();
      this._interactionController = controller;

      swatchLinks.forEach((swatchLink) => {
        swatchLink.addEventListener(
          "pointerenter",
          () => {
            void this.previewImage(swatchLink);
          },
          { signal: controller.signal },
        );

        swatchLink.addEventListener(
          "pointerleave",
          (event) => {
            const nextLink =
              event.relatedTarget instanceof Element
                ? event.relatedTarget.closest(".rcc-product-swatches__link")
                : null;

            if (nextLink && this.contains(nextLink)) return;
            this.restorePreviewImage();
          },
          { signal: controller.signal },
        );

        swatchLink.addEventListener(
          "focus",
          () => {
            void this.previewImage(swatchLink);
          },
          { signal: controller.signal },
        );

        swatchLink.addEventListener(
          "blur",
          () => {
            this.restorePreviewImage();
          },
          { signal: controller.signal },
        );
      });
    }

    getPrimaryCardImage() {
      return (
        this.closest(".rcc-search__product")?.querySelector(
          ".rcc-search__image--primary",
        ) ?? null
      );
    }

    getPriceElement() {
      return (
        this.closest(".rcc-search__product")?.querySelector(
          ".rcc-search__product__price",
        ) ?? null
      );
    }

    ensurePreviewState(primaryImage, priceElement) {
      const image =
        primaryImage instanceof HTMLImageElement ? primaryImage : null;
      const price = priceElement instanceof HTMLElement ? priceElement : null;

      if (
        this._previewState &&
        this._previewState.image === image &&
        this._previewState.priceElement === price
      ) {
        return this._previewState;
      }

      this._previewState = {
        image,
        src: image?.getAttribute("src") || "",
        srcset: image?.getAttribute("srcset") || "",
        alt: image?.getAttribute("alt") || "",
        priceElement: price,
        priceHtml: price?.innerHTML || "",
        priceAriaLabel: price?.getAttribute("aria-label") || "",
      };

      return this._previewState;
    }

    async previewImage(swatchLink) {
      const previewUrl = readString(swatchLink.dataset.previewUrl);
      const primaryImage = this.getPrimaryCardImage();
      const priceElement = this.getPriceElement();
      this.ensurePreviewState(primaryImage, priceElement);
      this.previewPrice(swatchLink);

      if (!previewUrl || !(primaryImage instanceof HTMLImageElement)) return;

      const previewToken = ++this._previewToken;
      await preloadImage(previewUrl);
      if (previewToken !== this._previewToken) return;

      primaryImage.src = previewUrl;
      primaryImage.removeAttribute("srcset");

      const previewAlt = readString(swatchLink.dataset.previewAlt);
      if (previewAlt) {
        primaryImage.alt = previewAlt;
      }
    }

    previewPrice(swatchLink) {
      const priceElement = this.getPriceElement();
      if (!(priceElement instanceof HTMLElement)) return;

      const priceAmount = readNumber(swatchLink.dataset.priceAmount);
      if (priceAmount == null) return;

      const compareAtAmount = readNumber(swatchLink.dataset.compareAtAmount);
      const moneyFormat =
        this.closest(".rcc-search")?.dataset.moneyFormat ||
        document.querySelector(".rcc-search")?.dataset.moneyFormat;

      const { html, ariaLabel } = renderPriceMarkup({
        priceAmount,
        compareAtAmount,
        moneyFormat,
      });

      priceElement.innerHTML = html;
      priceElement.setAttribute("aria-label", ariaLabel);
    }

    restorePreviewImage() {
      this._previewToken += 1;

      if (!this._previewState) return;

      const {
        image,
        src,
        srcset,
        alt,
        priceElement,
        priceHtml,
        priceAriaLabel,
      } = this._previewState;

      if (image instanceof HTMLImageElement) {
        if (src) {
          image.src = src;
        }

        if (srcset) {
          image.setAttribute("srcset", srcset);
        } else {
          image.removeAttribute("srcset");
        }

        image.alt = alt;
      }

      if (priceElement instanceof HTMLElement) {
        priceElement.innerHTML = priceHtml;

        if (priceAriaLabel) {
          priceElement.setAttribute("aria-label", priceAriaLabel);
        } else {
          priceElement.removeAttribute("aria-label");
        }
      }

      this._previewState = null;
    }

    async getStorefrontProduct(product) {
      const requestConfig = resolveProductRequest(
        product,
        getStorefrontContext(),
        "swatch",
      );
      if (!requestConfig) return null;

      if (swatchCache.has(requestConfig.cacheKey)) {
        return swatchCache.get(requestConfig.cacheKey);
      }

      if (pendingRequests.has(requestConfig.cacheKey)) {
        return pendingRequests.get(requestConfig.cacheKey);
      }

      const token = getStorefrontToken(this);
      if (!token) return null;

      const request = fetch(`/api/${API_VERSION}/graphql.json`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": token,
        },
        body: JSON.stringify({
          query: requestConfig.query,
          variables: requestConfig.variables,
        }),
      })
        .then((response) => response.json())
        .then((payload) => {
          if (payload.errors) {
            console.error(
              "Retail Cloud Connect swatch component query failed.",
              payload.errors,
            );
            return null;
          }

          const storefrontProduct = requestConfig.extractProduct(payload);
          swatchCache.set(requestConfig.cacheKey, storefrontProduct);
          return storefrontProduct;
        })
        .catch((error) => {
          console.error(
            "Retail Cloud Connect swatch component fetch failed.",
            error,
          );
          return null;
        })
        .finally(() => {
          pendingRequests.delete(requestConfig.cacheKey);
        });

      pendingRequests.set(requestConfig.cacheKey, request);
      return request;
    }

    getSwatches(linkedProducts) {
      return linkedProducts
        .map(normalizeLinkedProduct)
        .filter(
          (linkedProduct) => linkedProduct.label && linkedProduct.available,
        );
    }
  }

  function resolveProductRequest(product, context, intent) {
    const selection = getProductSelection(intent);
    const contextDirective = `@inContext(country: ${context.country}, language: ${context.language})`;
    const handle = resolveProductHandle(product);

    if (handle) {
      return {
        cacheKey: `handle:${handle}`,
        variables: { handle },
        query: `
          query ProductSwatchMetaByHandle($handle: String!) ${contextDirective} {
            product(handle: $handle) {
              ${selection}
            }
          }
        `,
        extractProduct: (payload) => payload.data?.product ?? null,
      };
    }

    const productId = resolveProductGid(product);
    if (productId) {
      return {
        cacheKey: `product:${productId}`,
        variables: { id: productId },
        query: `
          query ProductSwatchMetaById($id: ID!) ${contextDirective} {
            node(id: $id) {
              ... on Product {
                ${selection}
              }
            }
          }
        `,
        extractProduct: (payload) => payload.data?.node ?? null,
      };
    }

    const variantId = resolveVariantGid(product);
    if (!variantId) return null;

    return {
      cacheKey: `variant:${variantId}`,
      variables: { id: variantId },
      query: `
        query ProductSwatchMetaByVariant($id: ID!) ${contextDirective} {
          node(id: $id) {
            ... on ProductVariant {
              product {
                ${selection}
              }
            }
          }
        }
      `,
      extractProduct: (payload) => payload.data?.node?.product ?? null,
    };
  }

  function prioritizeCurrentSwatch(swatches, currentProductKey) {
    if (!currentProductKey) return swatches;

    const currentSwatchIndex = swatches.findIndex(
      (swatch) => swatch.compareKey === currentProductKey,
    );
    if (currentSwatchIndex <= 0) return swatches;

    return [
      swatches[currentSwatchIndex],
      ...swatches.slice(0, currentSwatchIndex),
      ...swatches.slice(currentSwatchIndex + 1),
    ];
  }

  function getProductSelection(intent) {
    if (intent !== "swatch") return "";

    return `
      id
      handle
      linkedColors: metafield(namespace: "wardow", key: "linked_colors") {
        references(first: 20) {
          nodes {
            ... on Product {
              id
              handle
              onlineStoreUrl
              title
              availableForSale
              featuredImage {
                url
              }
              priceRange {
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
              compareAtPriceRange {
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
              manufacturerColor: metafield(namespace: "option", key: "manufacturer_color") {
                value
              }
              colorCss: metafield(namespace: "wardow", key: "color_css") {
                value
              }
            }
          }
        }
      }
    `;
  }

  function resolveProductHandle(product) {
    const handle =
      readString(product?.handle) || readString(product?.product?.handle);
    if (handle) return handle;

    const url =
      readString(product?.onlineStoreUrl) ||
      readString(product?.url) ||
      readString(product?.product?.url);
    if (!url) return "";

    const match = url.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : "";
  }

  function resolveProductGid(product) {
    return normalizeGid(
      product?.id ||
        product?.productId ||
        product?.product?.id ||
        product?.product?.productId,
      "Product",
    );
  }

  function resolveVariantGid(product) {
    return normalizeGid(
      product?.variantId ||
        product?.selectedOrFirstAvailableVariantId ||
        product?.selectedVariantId ||
        product?.variant?.id,
      "ProductVariant",
    );
  }

  function resolveProductCompareKey(product) {
    const productId = resolveProductGid(product);
    if (productId) return `product:${productId}`;

    const handle = resolveProductHandle(product);
    return handle ? `handle:${handle}` : "";
  }

  function normalizeGid(value, resourceType) {
    if (typeof value === "number" && Number.isFinite(value)) {
      return `gid://shopify/${resourceType}/${value}`;
    }

    const normalizedValue = String(value || "").trim();
    if (!normalizedValue) return "";

    const gidMatch = normalizedValue.match(
      /^gid:\/\/shopify\/([A-Za-z]+)\/(\d+)$/,
    );
    if (gidMatch) {
      return gidMatch[1] === resourceType ? normalizedValue : "";
    }

    const resourceMatch = normalizedValue.match(
      /\/(Product|ProductVariant)\/(\d+)$/,
    );
    if (resourceMatch) {
      return resourceMatch[1] === resourceType
        ? `gid://shopify/${resourceMatch[1]}/${resourceMatch[2]}`
        : "";
    }

    return /^\d+$/.test(normalizedValue)
      ? `gid://shopify/${resourceType}/${normalizedValue}`
      : "";
  }

  function getStorefrontToken(element) {
    return (
      element.closest(".rcc-search")?.dataset.token ||
      document.querySelector(".rcc-search")?.dataset.token ||
      ""
    );
  }

  function getStorefrontContext() {
    const htmlLanguage = document.documentElement.lang || "de";
    const language = sanitizeEnum(
      window.Shopify?.locale || htmlLanguage.split("-")[0] || "de",
      "DE",
    );
    const country = sanitizeEnum(
      window.Shopify?.country || htmlLanguage.split("-")[1] || language,
      "DE",
    );

    return { country, language };
  }

  function sanitizeEnum(value, fallback) {
    const normalized = String(value || "")
      .trim()
      .slice(0, 2)
      .toUpperCase();
    return /^[A-Z]{2}$/.test(normalized) ? normalized : fallback;
  }

  function getArrayMetafield(product, namespace, key) {
    return normalizeMetafieldList(getMetafield(product, namespace, key));
  }

  function getMetafield(product, namespace, key) {
    const metafields = product?.metafields;
    if (!metafields) return null;

    if (metafields?.[namespace]?.[key] != null) {
      return metafields[namespace][key];
    }

    if (metafields?.[`${namespace}.${key}`] != null) {
      return metafields[`${namespace}.${key}`];
    }

    if (metafields?.[key] != null) {
      return metafields[key];
    }

    if (Array.isArray(metafields)) {
      return (
        metafields.find(
          (entry) => entry?.namespace === namespace && entry?.key === key,
        ) || null
      );
    }

    return null;
  }

  function normalizeMetafieldList(value, depth = 0) {
    if (!value || depth > 3) return [];

    if (Array.isArray(value)) {
      return value;
    }

    if (typeof value === "string") {
      const parsed = safeJsonParse(value);
      return parsed ? normalizeMetafieldList(parsed, depth + 1) : [];
    }

    if (Array.isArray(value.nodes)) {
      return value.nodes;
    }

    if (Array.isArray(value.edges)) {
      return value.edges.map((edge) => edge?.node).filter(Boolean);
    }

    if (value.references) {
      return normalizeMetafieldList(value.references, depth + 1);
    }

    if (value.value != null) {
      return normalizeMetafieldList(value.value, depth + 1);
    }

    if (Array.isArray(value.items)) {
      return value.items;
    }

    return [];
  }

  function safeJsonParse(value) {
    try {
      return JSON.parse(value);
    } catch (_error) {
      return null;
    }
  }

  function preloadImage(url) {
    if (!url) return Promise.resolve("");

    if (previewImageCache.has(url)) {
      return previewImageCache.get(url);
    }

    const request = new Promise((resolve) => {
      const image = new Image();
      image.onload = () => resolve(url);
      image.onerror = () => resolve(url);
      image.src = url;
    });

    previewImageCache.set(url, request);
    return request;
  }

  function renderPriceMarkup({ priceAmount, compareAtAmount, moneyFormat }) {
    const formattedPrice = formatPriceAmount(priceAmount, moneyFormat);
    const reduced = compareAtAmount != null && compareAtAmount > priceAmount;

    if (!reduced) {
      return {
        html: `<span class="rcc-search__product__price__now">${escapeHtml(formattedPrice)}</span>`,
        ariaLabel: `Price: ${formattedPrice}`,
      };
    }

    const formattedCompareAtPrice = formatPriceAmount(
      compareAtAmount,
      moneyFormat,
    );
    return {
      html: `<span class="rcc-search__product__price__now rcc-search__product__price__now--reduced">${escapeHtml(
        formattedPrice,
      )}</span><span aria-hidden="true" class="rcc-search__product__price__was">${escapeHtml(
        formattedCompareAtPrice,
      )}</span>`,
      ariaLabel: `Sale price: ${formattedPrice}, was ${formattedCompareAtPrice}`,
    };
  }

  function formatWithDelimiters(
    cents,
    precision = 2,
    thousands = ",",
    decimal = ".",
  ) {
    if (!Number.isFinite(cents)) return "";
    const number = (cents / 100).toFixed(precision);
    const parts = number.split(".");
    const price = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      `$1${thousands}`,
    );
    const remainder = parts[1] ? `${decimal}${parts[1]}` : "";
    return `${price}${remainder}`;
  }

  function formatPriceAmount(amount, moneyFormat) {
    if (amount == null || amount === "") return "";

    const cents = Number(amount) * 100;
    if (!Number.isFinite(cents)) return "";

    const formatString =
      typeof moneyFormat === "string"
        ? moneyFormat.replace(/<[^>]*>/g, "")
        : "{{amount}}";
    const match = formatString.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = match ? match[1] : undefined;

    let value;
    switch (placeholder) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
      case "amount_no_decimals_with_space_separator":
        value = formatWithDelimiters(cents, 0, " ", " ");
        break;
      case "amount_with_space_separator":
        value = formatWithDelimiters(cents, 2, " ", ".");
        break;
      case "amount_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 2, "'", ".");
        break;
      default:
        value = formatWithDelimiters(cents, 2);
        break;
    }

    return formatString.replace(/\{\{\s*(\w+)\s*\}\}/, value.toString());
  }

  function normalizeLinkedProduct(linkedProduct) {
    const available = resolveLinkedProductAvailability(linkedProduct);
    const swatchStyle = resolveSwatchStyle(
      resolveLinkedProductColorCss(linkedProduct),
    );
    const rawImageUrl = resolveLinkedProductImageUrl(linkedProduct);
    const thumbnailUrl = buildSizedImageUrl(rawImageUrl, 74);
    const previewUrl = buildSizedImageUrl(rawImageUrl, 480);
    const label = resolveLinkedProductLabel(linkedProduct);

    return {
      compareKey: resolveProductCompareKey(linkedProduct),
      url:
        readString(linkedProduct?.onlineStoreUrl) ||
        readString(linkedProduct?.url),
      label,
      background: swatchStyle.background,
      borderColor: swatchStyle.borderColor,
      imageUrl: thumbnailUrl,
      previewUrl,
      previewAlt: readString(linkedProduct?.title) || label,
      ...resolveLinkedProductPricing(linkedProduct),
      available: available !== false,
    };
  }

  function resolveLinkedProductColorCss(linkedProduct) {
    return (
      readString(linkedProduct?.colorCss?.value) ||
      readString(linkedProduct?.metafields?.wardow?.color_css?.value) ||
      readString(linkedProduct?.metafields?.wardow?.color_css)
    );
  }

  function resolveLinkedProductLabel(linkedProduct) {
    return (
      readString(linkedProduct?.manufacturerColor?.value) ||
      readString(
        linkedProduct?.metafields?.option?.manufacturer_color?.value,
      ) ||
      readString(linkedProduct?.metafields?.option?.manufacturer_color) ||
      readString(linkedProduct?.title)
    );
  }

  function resolveLinkedProductAvailability(linkedProduct) {
    return firstBoolean([
      linkedProduct?.availableForSale,
      linkedProduct?.available,
      linkedProduct?.product?.availableForSale,
      linkedProduct?.product?.available,
    ]);
  }

  function firstBoolean(values) {
    for (const value of values) {
      const normalizedValue = normalizeBoolean(value);
      if (normalizedValue !== null) return normalizedValue;
    }

    return null;
  }

  function normalizeBoolean(value) {
    if (typeof value === "boolean") return value;
    if (
      value &&
      typeof value === "object" &&
      typeof value.value === "boolean"
    ) {
      return value.value;
    }

    const normalizedValue = readString(value).toLowerCase();
    if (!normalizedValue) return null;
    if (
      normalizedValue === "false" ||
      normalizedValue === "0" ||
      normalizedValue === "no"
    )
      return false;
    if (
      normalizedValue === "true" ||
      normalizedValue === "1" ||
      normalizedValue === "yes"
    )
      return true;

    return null;
  }

  function resolveLinkedProductPricing(linkedProduct) {
    const priceAmount =
      readNumber(linkedProduct?.priceInfo?.price) ??
      readNumber(linkedProduct?.priceRange?.minVariantPrice?.amount) ??
      readNumber(linkedProduct?.price?.amount) ??
      readNumber(linkedProduct?.variants?.[0]?.price?.amount) ??
      readNumber(linkedProduct?.variantsAll?.[0]?.price?.amount);

    const compareAtAmount =
      readNumber(linkedProduct?.priceInfo?.originalPrice) ??
      readNumber(linkedProduct?.compareAtPriceRange?.minVariantPrice?.amount) ??
      readNumber(linkedProduct?.compareAtPrice?.amount) ??
      readNumber(linkedProduct?.variants?.[0]?.compareAtPrice?.amount) ??
      readNumber(linkedProduct?.variantsAll?.[0]?.compareAtPrice?.amount);

    const currencyCode =
      readString(linkedProduct?.priceInfo?.currencyCode) ||
      readString(linkedProduct?.priceRange?.minVariantPrice?.currencyCode) ||
      readString(linkedProduct?.price?.currencyCode) ||
      readString(linkedProduct?.variants?.[0]?.price?.currencyCode) ||
      readString(linkedProduct?.variantsAll?.[0]?.price?.currencyCode);

    return {
      priceAmount,
      compareAtAmount,
      currencyCode,
    };
  }

  function resolveLinkedProductImageUrl(linkedProduct) {
    return (
      readString(linkedProduct?.featuredImage?.url) ||
      readString(linkedProduct?.featured_image?.src) ||
      readString(linkedProduct?.image?.url) ||
      readString(linkedProduct?.image?.src) ||
      readString(linkedProduct?.img?.uri) ||
      readString(linkedProduct?.images?.[0]?.url) ||
      readString(linkedProduct?.images?.[0]?.uri)
    );
  }

  function buildSizedImageUrl(url, width) {
    const normalizedUrl = readString(url);
    if (!normalizedUrl) return "";

    try {
      const parsedUrl = new URL(normalizedUrl, window.location.origin);
      parsedUrl.searchParams.set("width", String(width));
      return parsedUrl.toString();
    } catch (_error) {
      return normalizedUrl;
    }
  }

  function resolveSwatchStyle(cssText) {
    const declarations = parseCssDeclarations(cssText);
    const background =
      getCssDeclarationValue(declarations, "background") ||
      getCssDeclarationValue(declarations, "background-color") ||
      getCssDeclarationValue(declarations, "color");

    return {
      background,
      borderColor:
        getCssDeclarationValue(declarations, "border-color") ||
        "rgba(0, 0, 0, 0.18)",
    };
  }

  function parseCssDeclarations(cssText) {
    const style = document.createElement("span").style;
    style.cssText = readString(cssText);
    return style;
  }

  function getCssDeclarationValue(declarations, property) {
    return declarations.getPropertyValue(property).trim();
  }

  function renderSwatchMarkup(swatch, currentProductKey) {
    const styles = [];

    if (swatch.background) {
      styles.push(
        `--rcc-product-swatches-bg: ${escapeAttribute(swatch.background)}`,
      );
    }

    styles.push(
      `--rcc-product-swatches-border: ${escapeAttribute(swatch.borderColor)}`,
    );

    const isCurrent =
      currentProductKey && swatch.compareKey === currentProductKey;
    const imageMarkup = swatch.imageUrl
      ? `<img
          class="rcc-product-swatches__image"
          src="${escapeAttribute(swatch.imageUrl)}"
          alt=""
          width="37"
          height="44"
          loading="lazy"
        >`
      : "";
    const itemMarkup = `
      <span
        class="rcc-product-swatches__item${swatch.imageUrl ? " has-image" : ""}${isCurrent ? " is-current" : ""}${
          swatch.available ? "" : " is-unavailable"
        }"
        style="${styles.join("; ")};"
      >
        <span class="rcc-product-swatches__color" aria-hidden="true"></span>
        ${imageMarkup}
      </span>
    `;

    if (swatch.url) {
      return `
        <a
          class="rcc-product-swatches__link"
          href="${escapeAttribute(swatch.url)}"
          aria-label="${escapeAttribute(swatch.label)}"
          title="${escapeAttribute(swatch.label)}"
          ${swatch.previewUrl ? `data-preview-url="${escapeAttribute(swatch.previewUrl)}"` : ""}
          ${swatch.previewAlt ? `data-preview-alt="${escapeAttribute(swatch.previewAlt)}"` : ""}
          ${swatch.priceAmount != null ? `data-price-amount="${escapeAttribute(swatch.priceAmount)}"` : ""}
          ${swatch.compareAtAmount != null ? `data-compare-at-amount="${escapeAttribute(swatch.compareAtAmount)}"` : ""}
          ${swatch.currencyCode ? `data-currency-code="${escapeAttribute(swatch.currencyCode)}"` : ""}
        >${itemMarkup}</a>
      `;
    }

    return `
      <span
        class="rcc-product-swatches__link"
        aria-label="${escapeAttribute(swatch.label)}"
        title="${escapeAttribute(swatch.label)}"
      >${itemMarkup}</span>
    `;
  }

  function readString(value) {
    if (typeof value === "string") return value.trim();
    if (typeof value === "number") return String(value);
    if (value && typeof value === "object") {
      if (typeof value.value === "string") return value.value.trim();
      if (typeof value.label === "string") return value.label.trim();
      if (typeof value.id === "string") return value.id.trim();
    }

    return "";
  }

  function readNumber(value) {
    const normalized = readString(value);
    if (!normalized) return null;

    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function escapeAttribute(value) {
    return escapeHtml(value).replace(/`/g, "&#96;");
  }

  if (!customElements.get(COMPONENT_NAME)) {
    customElements.define(COMPONENT_NAME, ProductSwatchesComponent);
  }
})();
